import {List} from 'immutable';

import {
  DynQueryParam,
  type DynQueryParamType,
  DynQuerySort,
  type DynQuerySortType,
} from '@packages/systems/dynamo/utils/ParamPanelUtils';

import {reportBug} from '@packages/utilities/reporter';

import {findBindingSourceByFieldPath} from '@packages/systems/dynamo/utils/ParamPanelUtils';
import {
  type OperatorId,
  getOperatorById,
  getOperatorsByName,
  OPERATOR_EQ,
  OPERATOR_GTE,
  OPERATOR_LTE,
  OPERATOR_TRUE,
} from '@packages/systems/dynamo/utils/DynQueryUtils';
import {FUTURE, PAST} from '@packages/systems/dynamo/constants';
import type {
  DeprecatedConditionsT,
  ConditionT,
  ConditionsT,
} from '@packages/systems/dynamo/types';
import {flatMap} from '@packages/utilities/fp/utils';
import type {
  ConsolidatedBindingContext,
  BindingSource,
} from '@packages/systems/dynamo/gateway';
import {SortData} from '@packages/systems/dynamo/utils/CollectionUtils';

// This needs to handle whether fields returns as an object due to being
// of the type Map<string, Map<string, mixed>>, or an Array<{ field, operator, value }>
export function parseFields(
  fields?: ConditionsT | DeprecatedConditionsT
): ConditionT[] {
  if (!fields) {
    return [];
  }

  if (Array.isArray(fields)) {
    return fields;
  }

  // TODO: This logical branch will be deprecated. Can be removed when we can
  // guarantee that all data.dyn.query.filter.fields is an array instead of an object
  return parseDeprecatedFields(fields);
}

const DEPRECATED_VALUE_NO_OPERATOR_NAME = 'DEPRECATED_VALUE_NO_OPERATOR_NAME';

function parseDeprecatedFields(fields: DeprecatedConditionsT): ConditionT[] {
  const mapper = ([fieldPath, operation]: [any, any]) => {
    if (!operation) {
      return [];
    }

    if (typeof operation !== 'object') {
      return [
        {
          fieldPath,
          operatorName: DEPRECATED_VALUE_NO_OPERATOR_NAME,
          value: operation,
        },
      ];
    }

    return Object.entries(operation).map(
      ([operatorName, value]: [any, any]) => ({
        fieldPath,
        operatorName,
        value,
      })
    );
  };

  return flatMap(mapper)(Object.entries(fields));
}

const getOperatorOverride = (
  opName: string,
  opValue: string | boolean | number | null
) => {
  const matchingOperator = getOperatorsByName(opName).find(
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'OperatorType'. | TS2339 - Property 'value' does not exist on type 'OperatorType'.
    (operator) => operator.value !== undefined && operator.value === opValue
  );

  return matchingOperator ? matchingOperator.id : null;
};

function normalizeOperatorOverride(
  opName: string,
  opValue: string | boolean | number | null,
  fieldPath: string | null,
  bindingSources: BindingSource[] | null
) {
  const operatorOverride = getOperatorOverride(opName, opValue);
  if (operatorOverride) {
    return operatorOverride;
  }

  if (opValue === null && fieldPath !== null && bindingSources !== null) {
    const bindingSource = findBindingSourceByFieldPath(
      bindingSources,
      fieldPath
    );
    if (bindingSource?.type === 'Bool') {
      return getOperatorById(OPERATOR_TRUE).id;
    }
  }

  return getOperatorsByName(opName)[0]?.id;
}

const safeGetOperatorName = (operatorId: OperatorId): string | undefined => {
  const operator = getOperatorById(operatorId);
  return operator ? operator.name : undefined;
};

const handleOldParamField = (
  fieldPath: string,
  value: string | number | null
): DynQueryParamType => {
  reportBug(
    'DynamoQueryOldFormat',
    'During query parse',
    {
      query: value,
      team: 'dynamo',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-852',
    },
    {skipConsole: true}
  );

  const operatorName = safeGetOperatorName(OPERATOR_EQ);

  return DynQueryParam({
    fieldPath,
    operator: operatorName
      ? normalizeOperatorOverride(operatorName, value, null, null)
      : undefined,
    value,
    value2: null,
    value3: null,
  });
};

const initializeParam = ({
  paramField: {fieldPath, operatorName, value},
  bindingSources,
}: {
  paramField: ConditionT;
  bindingSources: BindingSource[];
}): DynQueryParamType =>
  DynQueryParam({
    fieldPath,
    operator: normalizeOperatorOverride(
      operatorName,
      value,
      fieldPath,
      bindingSources
    ),
    value,
    value2: null,
    value3: null,
  });

const REGEX_MATCHES_TO_TENSES_MAP = {
  ago: PAST,
  future: FUTURE,
  'from now': FUTURE,
  past: PAST,
} as const;

const isValidTense = (
  tense: string | undefined
): tense is keyof typeof REGEX_MATCHES_TO_TENSES_MAP =>
  tense !== undefined && tense in REGEX_MATCHES_TO_TENSES_MAP;

export const parseLengthIntervalAndTense = (
  operatorValue: string
): {
  length: number;
  interval: string | undefined;
  tense: string;
} | null => {
  const lengthAndInterval = operatorValue.match(
    /^([0-9]+) (days|weeks|months|years)/
  );

  if (!lengthAndInterval) {
    return null;
  }

  const tense = operatorValue.match(/in the (past|future)$/);
  // handles deprecated forms of tense
  const oldTense = operatorValue.match(/(ago|from now)/);
  // If tense exists, return the match,
  // else if oldTense exists, return the match,
  // else flow requires undefined
  const tenseKey = tense ? tense[1] : oldTense ? oldTense[1] : undefined;
  const [, length, interval] = lengthAndInterval;
  return {
    length: parseInt(length ?? '0', 10) || 0,
    interval,
    tense: isValidTense(tenseKey)
      ? REGEX_MATCHES_TO_TENSES_MAP[tenseKey]
      : PAST,
  };
};

const handlePastOrFutureParam = (
  operator: '<=' | '>=',
  param: DynQueryParamType,
  fieldPath: string,
  // @ts-expect-error - TS7006 - Parameter 'operatorValue' implicitly has an 'any' type.
  operatorValue
) => {
  operatorValue = String(operatorValue);
  const parsed = parseLengthIntervalAndTense(operatorValue);
  if (!parsed) {
    return param;
  }
  const {length, interval, tense} = parsed;

  return DynQueryParam({
    fieldPath,
    operator,
    value: length,
    value2: interval,
    value3: tense,
  });
};

const handleFieldTypeDate = (
  param: DynQueryParamType,
  fieldPath: string,
  value: Record<any, any>
): DynQueryParamType => {
  const gteOperatorName = safeGetOperatorName(OPERATOR_GTE);
  const lteOperatorName = safeGetOperatorName(OPERATOR_LTE);
  const {operator} = param;

  if (operator === OPERATOR_GTE) {
    const gteValue = gteOperatorName ? value[gteOperatorName] : undefined;

    // Today
    if (gteValue === 'today') {
      return DynQueryParam({
        fieldPath,
        operator: OPERATOR_GTE,
        value: 0,
        value2: 'days',
        value3: PAST,
      });
    }

    if (gteValue === 'tomorrow') {
      return DynQueryParam({
        fieldPath,
        operator: OPERATOR_GTE,
        value: 1,
        value2: 'days',
        value3: FUTURE,
      });
    }

    return handlePastOrFutureParam(OPERATOR_GTE, param, fieldPath, gteValue);
  }

  if (operator === OPERATOR_LTE) {
    const lteValue = lteOperatorName ? value[lteOperatorName] : undefined;

    // Today/End of today
    // "end of today" is the value that will arrive from older date ranges
    if (lteValue === 'today' || lteValue === 'end of today') {
      return DynQueryParam({
        fieldPath,
        operator: OPERATOR_LTE,
        value: 0,
        value2: 'days',
        value3: PAST,
      });
    }

    if (lteValue === 'end of yesterday') {
      return DynQueryParam({
        fieldPath,
        operator: OPERATOR_LTE,
        value: 1,
        value2: 'days',
        value3: PAST,
      });
    }

    return handlePastOrFutureParam(OPERATOR_LTE, param, fieldPath, lteValue);
  }

  return param;
};

function parseParam(
  paramField: ConditionT,
  bindingSources: BindingSource[]
): DynQueryParamType {
  const {fieldPath, operatorName, value} = paramField;

  // Old style operator; no longer outputted
  if (operatorName === DEPRECATED_VALUE_NO_OPERATOR_NAME) {
    return handleOldParamField(fieldPath, value);
  }

  // New style operator
  const param = initializeParam({paramField, bindingSources});

  const bindingSource = findBindingSourceByFieldPath(bindingSources, fieldPath);
  const fieldType = bindingSource ? bindingSource.type : null;

  if (fieldType === 'Date') {
    return handleFieldTypeDate(param, fieldPath, {[operatorName]: value});
  }

  return param;
}

export function parseQuery(
  json: ConditionsT | DeprecatedConditionsT,
  bindingContext: ConsolidatedBindingContext | null
): List<DynQueryParamType> {
  const bindingSources = bindingContext
    ? bindingContext.getExpandedBindingSources()
    : [];

  const params = parseFields(json).map((param) =>
    parseParam(param, bindingSources)
  );
  return List(params);
}

/**
 * Parses a DomNode.data-based sort list into an Immutable.List(DynQuerySort)
 * (sourced from domNode.data.dyn.query.sort) for consumption by the UI.
 */
export const parseSorts = (sorts: Array<SortData>): List<DynQuerySortType> => {
  const list = sorts
    .map((sort) => {
      const ascending = sort.direction === 'asc';
      const property = sort.field;

      return DynQuerySort({property, ascending});
    })
    .filter((item): item is DynQuerySortType => item !== null);

  return List(list);
};
